import React, { FC, useEffect, useRef, useState } from "react";

import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { CircuitBreakerThresholds } from "../store/pomeriumApi";
import SettingSection from "./SettingSection";

const maxUint32 = 4294967295;
const thresholdDefaults = {
  maxConnections: 1024,
  maxPendingRequests: 1024,
  maxRequests: 1024,
  maxRetries: 3,
  maxConnectionPools: "Unlimited",
};

type ThresholdMode = "default" | "unlimited" | "custom";

function toThresholdMode(
  thresholds: CircuitBreakerThresholds,
  field: keyof CircuitBreakerThresholds,
): ThresholdMode {
  return thresholds[field] === undefined
    ? "default"
    : thresholds[field] === maxUint32
      ? "unlimited"
      : "custom";
}
function toThresholdString(
  mode: ThresholdMode,
  thresholds: CircuitBreakerThresholds,
  field: keyof CircuitBreakerThresholds,
): string {
  return mode === "default"
    ? `${thresholdDefaults[field]}`
    : mode === "unlimited"
      ? "Unlimited"
      : `${thresholds[field]}`;
}

type ThresholdProps = {
  name: string;
  value: CircuitBreakerThresholds;
  onChange: (thresholds?: CircuitBreakerThresholds) => void;
  field: keyof CircuitBreakerThresholds;
};
const Threshold: FC<ThresholdProps> = ({ name, value, field, onChange }) => {
  const [mode, setMode] = useState(toThresholdMode(value, field));
  const [localValue, setLocalValue] = useState(
    toThresholdString(mode, value, field),
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const error =
    mode === "custom"
      ? isNaN(Number(localValue))
        ? "invalid threshold"
        : undefined
      : undefined;
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    if (mode === "custom") {
      inputRef.current?.focus();
    }
  }, [mode]);
  useEffect(() => {
    const newValue = { ...value };
    switch (mode) {
      case "default":
        delete newValue[field];
        break;
      case "unlimited":
        newValue[field] = maxUint32;
        break;
      case "custom":
        {
          const n = Number(localValue);
          if (isNaN(n)) {
            return;
          }
          newValue[field] = n;
        }
        break;
    }
    onChange(newValue);
  }, [mode, localValue]);

  const handleChangeMode = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = { ...value };
    if (evt.target.value === "custom") {
      newValue[field] =
        field === "maxConnectionPools" ? 1024 : thresholdDefaults[field];
    }
    setLocalValue(
      toThresholdString(evt.target.value as ThresholdMode, newValue, field),
    );
    setMode(evt.target.value as ThresholdMode);
  };
  const handleChangeTextField = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(evt.target.value);
  };

  return (
    <>
      <Stack
        direction={smDown ? "column" : "row"}
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        gap={smDown ? 0 : 2}
      >
        <Typography
          variant="body1"
          sx={{ minWidth: smDown ? undefined : "175px" }}
        >
          {name}
        </Typography>
        <span className="flex grow">
          <RadioGroup row value={mode} onChange={handleChangeMode}>
            <FormControlLabel
              value="default"
              control={<Radio />}
              label="Default"
            />
            {field !== "maxRetries" && field !== "maxConnectionPools" && (
              <FormControlLabel
                value="unlimited"
                control={<Radio />}
                label="Unlimited"
              />
            )}
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom"
            />
          </RadioGroup>
        </span>
        <TextField
          inputRef={inputRef}
          disabled={mode !== "custom"}
          value={localValue}
          onChange={handleChangeTextField}
          size="small"
          error={!!error}
          helperText={error}
        />
      </Stack>
    </>
  );
};

type CircuitBreakerThresholdsMode = "custom" | "default";

type CircuitBreakerThresholdsSettingProps = {
  isGlobal?: boolean;
  value?: CircuitBreakerThresholds;
  onChange: (thresholds?: CircuitBreakerThresholds) => void;
};
const CircuitBreakerThresholdsSetting: FC<
  CircuitBreakerThresholdsSettingProps
> = ({ isGlobal, value, onChange }) => {
  const mode: CircuitBreakerThresholdsMode = value ? "custom" : "default";

  const handleChangeMode = (
    _evt: React.MouseEvent<HTMLElement>,
    newCustomizeValue: string,
  ) => {
    if (newCustomizeValue === "custom") {
      onChange({});
    } else {
      onChange();
    }
  };

  return (
    <SettingSection
      label="Circuit Breaker Thresholds"
      fullWidthChildren={
        mode === "custom" &&
        value && (
          <Stack spacing={2} alignItems="end" sx={{ width: "100%" }}>
            <Threshold
              name="Max Connections"
              value={value}
              onChange={onChange}
              field="maxConnections"
            />
            <Threshold
              name="Max Pending Requests"
              value={value}
              onChange={onChange}
              field="maxPendingRequests"
            />
            <Threshold
              name="Max Requests"
              value={value}
              onChange={onChange}
              field="maxRequests"
            />
            <Threshold
              name="Max Retries"
              value={value}
              onChange={onChange}
              field="maxRetries"
            />
            <Threshold
              name="Max Connection Pools"
              value={value}
              onChange={onChange}
              field="maxConnectionPools"
            />
          </Stack>
        )
      }
    >
      <Stack direction="row" justifyContent="flex-end">
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleChangeMode}
          size="medium"
        >
          <ToggleButton value="default">
            Use {isGlobal ? "Default Thresholds" : "Global Thresholds"}
          </ToggleButton>
          <ToggleButton value="custom">Custom Thresholds</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </SettingSection>
  );
};
export default CircuitBreakerThresholdsSetting;
